/**
 * LSAT percentile table.
 *
 * Source: LSAC, "LSAT Percentiles", LSAT Percentile Table: 2023-2026 Testing Years (the
 * 2023-2024, 2024-2025 and 2025-2026 testing years),
 * https://www.lsac.org/data-research/data/lsat-percentiles. Retrieved 2026-10-16 (parsed from
 * the page's HTML table, 61 rows). LSAC: "The percentile rank for any given test score ... is
 * the percentage of test scores that are lower than the given scaled score." LSAC recalculates
 * the table as new testing years accrue, so figures change when the window moves.
 *
 * Columns: score, percent below to hundredths, to tenths, to whole numbers (all as LSAC
 * prints them; the whole-number column tops out at 99).
 */

export const LSAT_TABLE_YEARS = "2023-2024, 2024-2025 and 2025-2026";
export const LSAT_MIN = 120;
export const LSAT_MAX = 180;

/** [score, hundredths, tenths, whole], 180 down to 120. */
const RAW: readonly (readonly [number, number, number, number])[] = [
  [180, 99.85, 99.8, 99],
  [179, 99.74, 99.7, 99],
  [178, 99.57, 99.6, 99],
  [177, 99.34, 99.3, 99],
  [176, 99.08, 99.1, 99],
  [175, 98.72, 98.7, 99],
  [174, 98.24, 98.2, 98],
  [173, 97.59, 97.6, 98],
  [172, 96.72, 96.7, 97],
  [171, 95.77, 95.8, 96],
  [170, 94.48, 94.5, 94],
  [169, 93.03, 93.0, 93],
  [168, 91.36, 91.4, 91],
  [167, 89.53, 89.5, 90],
  [166, 87.49, 87.5, 87],
  [165, 85.17, 85.2, 85],
  [164, 82.75, 82.8, 83],
  [163, 80.05, 80.1, 80],
  [162, 77.2, 77.2, 77],
  [161, 74.22, 74.2, 74],
  [160, 71.06, 71.1, 71],
  [159, 67.93, 67.9, 68],
  [158, 64.48, 64.5, 64],
  [157, 61.04, 61.0, 61],
  [156, 57.62, 57.6, 58],
  [155, 54.03, 54.0, 54],
  [154, 50.43, 50.4, 50],
  [153, 46.93, 46.9, 47],
  [152, 43.37, 43.4, 43],
  [151, 39.81, 39.8, 40],
  [150, 36.56, 36.6, 37],
  [149, 33.24, 33.2, 33],
  [148, 30.07, 30.1, 30],
  [147, 27.06, 27.1, 27],
  [146, 24.2, 24.2, 24],
  [145, 21.48, 21.5, 21],
  [144, 19.04, 19.0, 19],
  [143, 16.73, 16.7, 17],
  [142, 14.61, 14.6, 15],
  [141, 12.66, 12.7, 13],
  [140, 11.0, 11.0, 11],
  [139, 9.44, 9.4, 9],
  [138, 8.05, 8.1, 8],
  [137, 6.89, 6.9, 7],
  [136, 5.86, 5.9, 6],
  [135, 4.96, 5.0, 5],
  [134, 4.22, 4.2, 4],
  [133, 3.57, 3.6, 4],
  [132, 3.02, 3.0, 3],
  [131, 2.54, 2.5, 3],
  [130, 2.17, 2.2, 2],
  [129, 1.85, 1.8, 2],
  [128, 1.58, 1.6, 2],
  [127, 1.37, 1.4, 1],
  [126, 1.2, 1.2, 1],
  [125, 1.04, 1.0, 1],
  [124, 0.93, 0.9, 1],
  [123, 0.83, 0.8, 1],
  [122, 0.76, 0.8, 1],
  [121, 0.66, 0.7, 1],
  [120, 0.0, 0.0, 0],
];

export interface LsatRow {
  score: number;
  /** Percent of scores lower, to two decimals. */
  hundredths: number;
  tenths: number;
  whole: number;
}

export const LSAT_ROWS: readonly LsatRow[] = RAW.map(
  ([score, hundredths, tenths, whole]) => ({ score, hundredths, tenths, whole })
);

/** Whether the value is a possible LSAT score (a whole number from 120 to 180). */
export function isLsatScore(value: number | null): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= LSAT_MIN &&
    value <= LSAT_MAX
  );
}

export function lsatRow(score: number): LsatRow | null {
  return LSAT_ROWS.find((row) => row.score === score) ?? null;
}

/** The lowest score whose percent-below is at least `target`, or null above the top row. */
export function lsatLowestForPercentile(target: number): number | null {
  let best: number | null = null;
  for (const row of LSAT_ROWS) {
    if (row.hundredths >= target && (best === null || row.score < best)) {
      best = row.score;
    }
  }
  return best;
}

/** The gap in percent-below between two scores, in hundredths of a point. */
export function lsatGap(from: number, to: number): number | null {
  const a = lsatRow(from);
  const b = lsatRow(to);
  if (a === null || b === null) {
    return null;
  }
  return Math.round((b.hundredths - a.hundredths) * 100) / 100;
}
